import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    AccordionBody,
    AccordionHeader,
    AccordionItem,
    Dropdown,
    DropdownItem,
    DropdownMenu,
    DropdownToggle,
    UncontrolledAccordion,
} from 'reactstrap'
import { DataTable, useDataTable, useTableState } from '../../../../Components/TanstackTable'
import useHasBeenVisible from '../../../../hooks/useHasBeenVisible'
import { useConnectedProperties } from './useConnectedProperties'
import useTableColumns from './useTableColumns'
import { ConnectedPropertiesFilterData } from './utils'

interface ConnectedPropertiesAccordionProps {
    contactId?: number
}

const ConnectedPropertiesAccordion = ({ contactId }: ConnectedPropertiesAccordionProps) => {
    const { t } = useTranslation()

    const [dropdownOpen, setDropdownOpen] = useState(false)
    const [dropDownText, setDropDownText] = useState<string>(t('accordion.connectedProperties.dropdown.placeholder'))
    const [selectedFilter, setSelectedFilter] = useState<keyof ConnectedPropertiesFilterData | 'all'>('all')

    const { ref, hasBeenVisible } = useHasBeenVisible()
    const { filterData, isLoading } = useConnectedProperties(contactId, hasBeenVisible)

    const tableData = useMemo(() => {
        if (!filterData) return []

        return selectedFilter === 'all' ? Object.values(filterData).flat() : (filterData[selectedFilter] ?? [])
    }, [filterData, selectedFilter])

    const columns = useTableColumns()
    const tableState = useTableState()
    const { table } = useDataTable({ ...tableState, columns, data: tableData })

    const toggle = () => setDropdownOpen((prevState) => !prevState)

    return (
        <div ref={ref}>
            <UncontrolledAccordion defaultOpen={['1']} stayOpen toggle={() => null}>
                <AccordionItem className="mb-4">
                    <div className="position-relative">
                        <AccordionHeader targetId="1">
                            <h5 className="card-title my-2">{t('app.common.relatedProperties')}</h5>
                        </AccordionHeader>
                        {/* content inside div can't be inside AccordionHeader - warning appears: <button> cannot appear as a descendant of <button>  */}
                        <div className="position-absolute d-flex align-items-center" style={{ top: '10px', left: '180px', zIndex: 15 }}>
                            <Dropdown
                                isOpen={dropdownOpen}
                                toggle={toggle}
                                direction="down"
                                onClick={(e) => {
                                    e.stopPropagation()
                                }}
                            >
                                {tableData.length > 0 && (
                                    <DropdownToggle tag="button" className="btn btn-light border border-2 d-flex justify-content-between">
                                        {dropDownText} <i className="mdi mdi-chevron-down ms-2"></i>
                                    </DropdownToggle>
                                )}
                                <DropdownMenu className="w-100">
                                    <DropdownItem
                                        onClick={(e) => {
                                            setDropDownText(e.currentTarget.textContent ?? '')
                                            setSelectedFilter('all')
                                        }}
                                    >
                                        {t('accordion.connectedProperties.dropdown.all')}
                                    </DropdownItem>
                                    {filterData &&
                                        Object.entries(filterData).map(
                                            ([key, array]) =>
                                                array.length > 0 && (
                                                    <DropdownItem
                                                        key={key}
                                                        onClick={(e) => {
                                                            setDropDownText(e.currentTarget.textContent ?? '')
                                                            setSelectedFilter(key as keyof ConnectedPropertiesFilterData)
                                                        }}
                                                    >
                                                        {t(`accordion.connectedProperties.dropdown.${key}`)}
                                                    </DropdownItem>
                                                )
                                        )}
                                </DropdownMenu>
                            </Dropdown>
                        </div>
                    </div>
                    <AccordionBody accordionId="1">
                        <DataTable
                            table={table}
                            isLoading={isLoading}
                            theadClass="dmn-table-header"
                            noResultMessage={t('table.noResultConnectedProperties')}
                        />
                    </AccordionBody>
                </AccordionItem>
            </UncontrolledAccordion>
        </div>
    )
}

export default ConnectedPropertiesAccordion
